'use client';

import { useEffect, useState } from 'react';
import { queryMonitor, QueryPerformanceMetrics } from '@/lib/supabase/queryHelpers';
import Icon from '@/components/ui/AppIcon';

/**
 * Database Query Performance Monitor Component
 * Displays real-time query performance metrics in development mode
 * Shows query count, execution time, and slow query warnings
 */
export default function QueryPerformanceMonitor() {
  const [metrics, setMetrics] = useState<QueryPerformanceMetrics | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Only show in development mode
  const isDev = process.env.NODE_ENV === 'development';

  useEffect(() => {
    if (!isDev) return;

    // Update metrics every 2 seconds
    const interval = setInterval(() => {
      const currentMetrics = queryMonitor?.getMetrics();
      setMetrics(currentMetrics);
    }, 2000);

    return () => clearInterval(interval);
  }, [isDev]);

  if (!isDev || !metrics || metrics?.queryCount === 0) return null;

  const avgQueryTime = metrics?.totalExecutionTime / metrics?.queryCount;
  const slowQueries = metrics?.queries?.filter((q) => q?.executionTime > 500);

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Collapsed View */}
      {!isExpanded && (
        <button
          onClick={() => setIsExpanded(true)}
          className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-lg hover:bg-gray-800 transition-colors"
        >
          <Icon name="ChartBarIcon" className="w-5 h-5" />
          <div className="text-left">
            <div className="text-xs text-gray-400">DB Queries</div>
            <div className="text-sm font-semibold">
              {metrics?.queryCount} queries • {metrics?.totalExecutionTime?.toFixed(0)}ms
            </div>
          </div>
          {slowQueries?.length > 0 && (
            <div className="bg-yellow-500 text-black text-xs font-bold px-2 py-1 rounded">
              {slowQueries?.length} slow
            </div>
          )}
        </button>
      )}
      {/* Expanded View */}
      {isExpanded && (
        <div className="bg-gray-900 text-white rounded-lg shadow-2xl w-96 max-h-96 overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-700">
            <div className="flex items-center gap-2">
              <Icon name="ChartBarIcon" className="w-5 h-5 text-blue-400" />
              <h3 className="font-semibold">Query Performance</h3>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  queryMonitor?.reset();
                  setMetrics(null);
                }}
                className="text-gray-400 hover:text-white transition-colors"
                title="Reset metrics"
              >
                <Icon name="ArrowPathIcon" className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsExpanded(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <Icon name="XMarkIcon" className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="p-4 bg-gray-800 grid grid-cols-3 gap-4">
            <div>
              <div className="text-xs text-gray-400">Total Queries</div>
              <div className="text-xl font-bold text-blue-400">{metrics?.queryCount}</div>
            </div>
            <div>
              <div className="text-xs text-gray-400">Total Time</div>
              <div className="text-xl font-bold text-green-400">
                {metrics?.totalExecutionTime?.toFixed(0)}ms
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-400">Avg Time</div>
              <div className="text-xl font-bold text-purple-400">
                {avgQueryTime?.toFixed(0)}ms
              </div>
            </div>
          </div>

          {/* Slow Queries Warning */}
          {slowQueries?.length > 0 && (
            <div className="p-3 bg-yellow-500/10 border-l-4 border-yellow-500 mx-4 mt-4 rounded">
              <div className="flex items-center gap-2 text-yellow-500">
                <Icon name="ExclamationTriangleIcon" className="w-5 h-5" />
                <span className="text-sm font-semibold">
                  {slowQueries?.length} slow {slowQueries?.length === 1 ? 'query' : 'queries'} detected (&gt;500ms)
                </span>
              </div>
            </div>
          )}

          {/* Query List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            <div className="text-xs text-gray-400 uppercase font-semibold mb-2">Recent Queries</div>
            {metrics?.queries?.slice(-10)?.reverse()?.map((query, index) => (
              <div
                key={index}
                className={`p-2 rounded text-xs ${
                  query?.executionTime > 500
                    ? 'bg-yellow-500/10 border border-yellow-500/30' :'bg-gray-800'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-blue-400">{query?.table}</span>
                  <span
                    className={`font-mono ${
                      query?.executionTime > 500 ? 'text-yellow-400' : 'text-green-400'
                    }`}
                  >
                    {query?.executionTime?.toFixed(2)}ms
                  </span>
                </div>
                <div className="text-gray-400">{query?.operation}</div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="p-3 bg-gray-800 border-t border-gray-700 text-xs text-gray-400 text-center">
            Dev mode only • Updates every 2s
          </div>
        </div>
      )}
    </div>
  );
}